import React, { useEffect, useRef, useState } from "react";
import { Loader2, Play, Save, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VoiceTrainer } from "@/components/VoiceTrainer";
import { SETTINGS, FX, Knob, useTake, TakePlayer } from "@/components/voiceKit";

const Step = ({ n, title, help, children }) => (
  <section className="border border-border rounded-xl p-4 space-y-3" data-testid={`voice-step-${n}`}>
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 rounded-full bg-primary text-white text-sm font-bold flex items-center justify-center shrink-0">{n}</span>
      <div><div className="font-bold text-sm">{title}</div>{help && <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{help}</p>}</div>
    </div>
    {children}
  </section>
);

export const VoiceEditor = ({ voice, presets, sampleText, onSave, onClose }) => {
  const [v, setV] = useState(voice);
  const [text, setText] = useState(sampleText);
  const take = useTake();
  const fxTimer = useRef(null);
  const set = (patch) => setV((p) => ({ ...p, ...patch }));
  const body = (st = v.settings) => ({ preset: v.preset, source: v.source || "preset", voice_id: v.id || "", text, gender: v.gender || "", settings: st });
  const generate = (st) => take.generate(body(st), v.fx);
  const setFx = (k, x) => {
    const fx = { ...v.fx, [k]: x };
    set({ fx });
    clearTimeout(fxTimer.current);
    fxTimer.current = setTimeout(() => take.applyFx(fx), 350);
  };
  const setSetting = (k, x) => set({ settings: { ...v.settings, [k]: x } });
  useEffect(() => () => clearTimeout(fxTimer.current), []);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover border-border max-w-3xl max-h-[92vh] overflow-y-auto space-y-1" dir="rtl" data-testid="voice-editor">
        <DialogHeader><DialogTitle>{v.id ? `تدريب وضبط شخصية ${voice.name}` : "شخصية صوتية جديدة"}</DialogTitle><DialogDescription>اتبع الخطوات من 1 إلى 5</DialogDescription></DialogHeader>
        <Step n={1} title="الاسم والنوع" help="الاسم يظهر للعملاء عند اختيار الشخصية. الصوت الأساسي هو نقطة البداية قبل التدريب.">
          <div className="grid sm:grid-cols-3 gap-3">
            <Input value={v.name} onChange={(e) => set({ name: e.target.value })} placeholder="اسم الشخصية (مثال: فهد)" className="bg-secondary border-border" data-testid="voice-name-input" />
            <Select value={v.gender} onValueChange={(x) => set({ gender: x })}>
              <SelectTrigger className="bg-secondary border-border" data-testid="voice-gender-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="male">رجل</SelectItem><SelectItem value="female">امرأة</SelectItem></SelectContent>
            </Select>
            <Select value={v.preset} onValueChange={(x) => set({ preset: x })}>
              <SelectTrigger className="bg-secondary border-border" data-testid="voice-preset-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{presets.map((p) => (typeof p === "string"
                ? <SelectItem key={p} value={p}>{p}</SelectItem>
                : <SelectItem key={p.id} value={p.id}>{p.name} — {p.gender === "female" ? "امرأة" : "رجل"} · {p.desc}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <Input value={v.persona || ""} onChange={(e) => set({ persona: e.target.value })} placeholder="متى يناسب هذا الصوت؟ (مثال: شاب حماسي للمطاعم والتقنية)" className="bg-secondary border-border" data-testid="voice-persona-input" />
        </Step>
        <Step n={2} title="ارفع أو سجّل صوت الشخصية (اختياري)" help="للحصول على لهجة سعودية حقيقية: سجّل 15–30 ثانية بكلام طبيعي بلهجتك، في مكان هادئ، بدون موسيقى، والجوال قريب من الفم.">
          <VoiceTrainer voice={v} onChange={set} />
        </Step>
        <Step n={3} title="الضبط" help="المؤثرات تتغير فوراً وتسمعها مباشرة بدون رصيد. إعدادات الأداء تحتاج توليد جديد تلقائي عند ترك المؤشر (يستهلك رصيد بسيط).">
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="space-y-4"><div className="font-bold text-xs text-primary">إعدادات الأداء</div>
              {SETTINGS.map(([k, l, a, b, s, h]) => <Knob key={k} k={k} label={l} min={a} max={b} step={s} help={h} badge="توليد" value={v.settings[k]}
                onChange={(x) => setSetting(k, x)} onCommit={(x) => take.rawId && generate({ ...v.settings, [k]: x })} />)}
            </div>
            <div className="space-y-4"><div className="font-bold text-xs text-primary">المؤثرات (فوري)</div>
              {FX.map(([k, l, a, b, s, h]) => <Knob key={k} k={k} label={l} min={a} max={b} step={s} help={h} value={v.fx[k]} onChange={(x) => setFx(k, x)} />)}
              <label className="flex items-center justify-between text-xs font-medium">صوت إذاعي (راديو)<Switch checked={!!v.fx.radio} onCheckedChange={(c) => setFx("radio", c)} data-testid="fx-radio" /></label>
            </div>
          </div>
        </Step>
        <Step n={4} title="التجربة" help="اكتب جملة إعلانية بلهجة سعودية واضغط توليد. بعدها أي تحريك للمؤثرات يشغّل الصوت مباشرة لتسمع الفرق.">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} className="bg-secondary border-border" data-testid="voice-test-text" />
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => generate()} disabled={take.busy} className="gap-2 border-border" data-testid="voice-preview-btn">
              {take.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : take.rawId ? <RefreshCw className="w-4 h-4" /> : <Play className="w-4 h-4" />} {take.busy ? "جارٍ التوليد…" : take.rawId ? "توليد من جديد" : "توليد وتجربة"}
            </Button>
            {take.fxBusy && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> تطبيق المؤثرات…</span>}
            {v.source === "clone" && <span className="text-xs text-muted-foreground">الصوت المستنسخ يأخذ دقيقة تقريباً</span>}
          </div>
          <TakePlayer take={take} testid="voice-preview-audio" />
        </Step>
        <Step n={5} title="الاعتماد" help="عند الحفظ تصبح الشخصية متاحة للعملاء بهذه الإعدادات، وتُحدَّث عيناتهم تلقائياً.">
          <Button onClick={() => onSave(v)} className="gap-2" data-testid="voice-save-btn"><Save className="w-4 h-4" /> حفظ واعتماد</Button>
        </Step>
      </DialogContent>
    </Dialog>
  );
};
